import { background, button, divider, lightBlue, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { Box, ButtonProps, CircularProgress, Button as MuiButton, css, styled } from "@mui/material";
import { HTMLAttributeAnchorTarget, MouseEvent, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

export type TButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "warning" | "danger";
export type TButtonSize = "lg" | "md" | "sm" | "xs";

export type TButtonProps = {
  variant?: TButtonVariant;
  size?: TButtonSize;
  to?: string;
  isLoading?: boolean;
  target?: HTMLAttributeAnchorTarget;
} & Omit<ButtonProps, "variant" | "size">;

export const Button: FCC<TButtonProps> = ({
  variant = "primary",
  size = "md",
  fullWidth,
  children,
  isLoading: isExternalLoading,
  onClick,
  ...rest
}) => {
  const [loading, setLoading] = useState(false);
  const methods = useFormContext();

  const handleOnclick = async (e: MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return;
    setLoading(true);
    await onClick(e);
    setLoading(false);
  };

  const isLoading = useMemo(() => {
    if (rest.type === "submit" && !!methods) return methods.formState.isSubmitting;
    return loading || isExternalLoading;
  }, [loading, methods, rest.type, isExternalLoading]);

  return (
    <StyledButton
      btnVariant={variant}
      btnSize={size}
      onClick={handleOnclick}
      sx={{ width: fullWidth ? "100%" : "fit-content" }}
      disabled={isLoading}
      {...rest}
    >
      {children}
      {isLoading && (
        <LoadingButton>
          <CircularProgress color="inherit" />
        </LoadingButton>
      )}
    </StyledButton>
  );
};

const options = { shouldForwardProp: (propName: string) => !["btnVariant", "btnSize"].includes(propName) };

const StyledButton = styled(MuiButton, options)<{ btnVariant: TButtonVariant; btnSize: TButtonSize }>`
  &.MuiButtonBase-root {
    line-height: 1;
    text-transform: none;
    position: relative;
    overflow: hidden;
    color: ${text.white};
    white-space: nowrap;
    min-width: 60px;
    cursor: pointer;
    ${({ btnVariant, btnSize }) => css`
      // variants
      ${PrimaryButtonCSS}
      ${btnVariant === "secondary" && SecondaryButtonCSS};
      ${btnVariant === "outline" && OutlineButtonCSS};
      ${btnVariant === "ghost" && GhostButtonCSS};
      ${btnVariant === "warning" && WarningButtonCSS};
      ${btnVariant === "danger" && DangerButtonCSS};

      // sizes
      ${MdButtonCSS}
      ${btnSize === "lg" && LgButtonCSS}
    ${btnSize === "sm" && SmButtonCSS}
    ${btnSize === "xs" && XsButtonCSS}
    `}
    :disabled, &.Mui-disabled {
      background: ${background.disable};
      border: 2px solid ${background.disable};
      color: ${text.tertiary};
    }
  }
`;

const PrimaryButtonCSS = css`
  background: ${button.primary};
  border: 2px solid ${button.primary};
  color: ${text.white};
  :hover {
    background: ${lightBlue[99]};
    color: ${button.primary};
  }
  :focus-visible {
    background: ${button.hover_02};
    color: ${button.primary};
  }
  :active {
    background: ${button.pressed};
    color: ${text.white};
    border: 2px solid ${button.pressed};
  }
`;

const SecondaryButtonCSS = css`
  background: ${background.white};
  border: 2px solid ${button.primary};
  color: ${button.primary};
  :hover {
    background: ${lightBlue[99]};
  }
  :focus-visible {
    background: ${button.hover_02};
  }
  :active {
    background: ${button.pressed};
    border: 2px solid ${button.pressed};
    color: ${text.white};
  }
`;

const OutlineButtonCSS = css`
  background: ${background.white};
  color: ${button.primary};
  border: 2px solid ${button.primary};
  :hover {
    background: ${button.hover_02};
  }
  :focus-visible {
    background: ${button.hover_02};
  }
  :active {
    background: ${button.hover_01};
    color: ${text.white};
    border: 2px solid ${button.pressed};
  }
`;

const GhostButtonCSS = css`
  background-color: ${background.white};
  color: ${text.secondary};
  border: 2px solid ${text.tertiary};
  :hover {
    background: ${divider.low};
    color: ${text.secondary};
  }
  :focus-visible {
    background: ${divider.middle};
    color: ${text.secondary};
  }
  :active {
    background: ${divider.high};
    color: ${text.secondary};
  }
`;

const WarningButtonCSS = css`
  background: ${button.secondary};
  border: 2px solid ${button.secondary};
  color: ${text.white};
  :hover {
    background: ${text.white};
    color: ${button.secondary};
  }
  :focus-visible {
    background: ${text.white};
    color: ${button.secondary};
  }
  :active {
    background: ${text.white};
    color: ${button.secondary};
  }
`;

const DangerButtonCSS = css`
  background-color: ${background.white};
  color: ${button.danger};
  border: 2px solid ${button.danger};
  :hover {
    background-color: ${button.danger_hover};
    color: ${button.danger};
  }
  :focus-visible {
    background-color: ${button.danger_hover};
    color: ${button.danger};
  }
  :active {
    background-color: ${button.danger_hover};
    color: ${button.danger};
    border: 2px solid ${button.danger};
  }
`;

const LgButtonCSS = css`
  height: 48px;
  padding: 0 30px;
  border-radius: ${rounded.sm};
  ${typography.head20Semi}
  .MuiCircularProgress-root {
    width: 20px !important;
    height: 20px !important;
  }
`;

const MdButtonCSS = css`
  height: 40px;
  padding: 0 22px;
  border-radius: ${rounded.sm};
  ${typography.sub16Semi}
  .MuiCircularProgress-root {
    width: 16px !important;
    height: 16px !important;
  }
`;

const SmButtonCSS = css`
  height: 37px;
  font-size: 14px !important;
  font-weight: 500;
  padding: 0 14px;
  border-radius: ${rounded.sm};
  ${typography.cap12Bold}
  .MuiCircularProgress-root {
    width: 12px !important;
    height: 12px !important;
  }
`;

const XsButtonCSS = css`
  height: 25px;
  padding: 0 6px;
  border-radius: ${rounded.lg};
  ${typography.cap12Bold}
  .MuiCircularProgress-root {
    width: 12px !important;
    height: 12px !important;
  }
`;

const LoadingButton = styled(Box)`
  position: absolute;
  display: block;
  top: 0;
  left: 0;
  background-color: inherit;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
